import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ExternalLink, LifeBuoy, Loader2, Star } from 'lucide-react'
import { MarketingShell } from './MarketingShell'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatRelative } from '@/lib/utils'
import { describeApiError } from '@/lib/apiError'
import { useCurrency } from '@/contexts/CurrencyContext'
import type { GuestFeedbackRow, RecoveryState } from '@/types'

/** Human labels for the reason slugs the low-score survey step stores.
 *  Mirrors reviewReasonCatalog in backend/internal/handlers/marketing_review_survey.go. */
const REASON_LABELS: Record<string, string> = {
  food: 'The food',
  service: 'The service',
  wait: 'The wait',
  cleanliness: 'Cleanliness',
  value: 'Value',
  other: 'Something else',
}

const STATE_STYLES: Record<RecoveryState, string> = {
  new: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  acknowledged: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  recovered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  closed: 'bg-muted text-muted-foreground',
}

const STATE_LABELS: Record<RecoveryState, string> = {
  new: 'Needs a reply',
  acknowledged: 'Acknowledged',
  recovered: 'Recovered',
  closed: 'Closed',
}

const PAGE_SIZE = 50

/**
 * Guest recovery — the worklist.
 *
 * Split from the Reviews settings page on purpose: this is shift work a
 * manager does while the guest may still be in the building, whereas the
 * settings (Google link, gating, receipt QR) are an owner's one-time setup and
 * stay admin-only. Route allowlisting matches by leaf path, so the split IS
 * the role boundary.
 */
export function AdminGuestRecovery() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { formatCurrency } = useCurrency()
  const [stateFilter, setStateFilter] = useState<'all' | RecoveryState>('all')
  const [ratingFilter, setRatingFilter] = useState<'all' | '2' | '3'>('all')
  const [pages, setPages] = useState(1)
  const [recovering, setRecovering] = useState<GuestFeedbackRow | null>(null)

  const listParams = useMemo(
    () => ({
      state: stateFilter === 'all' ? undefined : stateFilter,
      rating_max: ratingFilter === 'all' ? undefined : Number(ratingFilter),
      limit: PAGE_SIZE * pages,
    }),
    [stateFilter, ratingFilter, pages],
  )

  const inboxQuery = useQuery({
    queryKey: ['marketing', 'guest-feedback', listParams],
    queryFn: () => apiClient.listReviewFeedback(listParams),
    staleTime: 15_000,
    // A fresh one-star should surface without the manager reloading.
    refetchInterval: 60_000,
  })

  const feedback: GuestFeedbackRow[] = inboxQuery.data?.data?.feedback ?? []
  const filtersActive = stateFilter !== 'all' || ratingFilter !== 'all'
  // A full page means there is very likely more behind it. Without this the
  // oldest unresolved guests are simply unreachable once a tenant passes the
  // page size, while the Overview keeps counting them.
  const mayHaveMore = feedback.length >= PAGE_SIZE * pages

  const resetPaging = () => setPages(1)

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['marketing', 'guest-feedback'] })
    // The Overview's recovered-revenue card reads a different endpoint; without
    // this it keeps showing pre-action numbers until its own staleTime lapses.
    queryClient.invalidateQueries({ queryKey: ['marketing', 'recovery-summary'] })
  }

  const setState = useMutation({
    mutationFn: (v: { id: string; state: 'acknowledged' | 'closed' }) =>
      apiClient.updateGuestFeedbackState(v.id, v.state),
    onSuccess: invalidateAll,
    onError: (err: unknown) => {
      toast({
        title: 'Could not update',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  return (
    <MarketingShell
      title="Guest recovery"
      description="Every rating with the visit behind it — and a one-tap apology plus comeback coupon for the ones that went wrong."
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={ratingFilter}
            onValueChange={(v) => {
              setRatingFilter(v as typeof ratingFilter)
              resetPaging()
            }}
          >
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ratings</SelectItem>
              <SelectItem value="2">2 stars and under</SelectItem>
              <SelectItem value="3">3 stars and under</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={stateFilter}
            onValueChange={(v) => {
              setStateFilter(v as typeof stateFilter)
              resetPaging()
            }}
          >
            <SelectTrigger className="h-8 w-[170px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="new">Needs a reply</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="recovered">Recovered</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          {inboxQuery.isFetching && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
          )}
        </div>

        {inboxQuery.isError ? (
          <div className="rounded-lg border border-destructive/50 p-4 text-sm text-destructive">
            Couldn’t load guest feedback: {describeApiError(inboxQuery.error).message}
          </div>
        ) : inboxQuery.isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : feedback.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
            {filtersActive ? (
              <>
                <p>No feedback matches these filters.</p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setStateFilter('all')
                    setRatingFilter('all')
                    resetPaging()
                  }}
                >
                  Clear filters
                </Button>
              </>
            ) : (
              <p>
                Nothing yet. Turn on the receipt QR in Reviews, or point a
                poster at the feedback link — both land here.
              </p>
            )}
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {feedback.map((f) => (
                <li key={f.id} className="rounded-lg border border-border/80 bg-background p-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star
                          key={i}
                          className={`h-3.5 w-3.5 ${i < f.rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`}
                          aria-hidden
                        />
                      ))}
                    </span>
                    <span className="font-medium">{f.guest_name || 'Anonymous'}</span>
                    {f.guest_phone && (
                      <span className="text-muted-foreground">{f.guest_phone}</span>
                    )}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_STYLES[f.recovery_state]}`}
                    >
                      {STATE_LABELS[f.recovery_state]}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {f.created_at ? formatRelative(f.created_at) : ''}
                    </span>
                    {f.redirected && (
                      <span className="flex items-center gap-1 text-xs text-emerald-600">
                        <ExternalLink className="h-3 w-3" aria-hidden /> sent to Google
                      </span>
                    )}
                  </div>

                  {/* Order context — present only when the rating arrived on a
                      receipt QR, which is exactly when it's most actionable. */}
                  {f.order_number && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      #{f.order_number}
                      {typeof f.order_total === 'number' && ` · ${formatCurrency(f.order_total)}`}
                      {f.server_name && ` · served by ${f.server_name}`}
                      {f.order_type && ` · ${f.order_type.replace(/_/g, ' ')}`}
                    </div>
                  )}

                  {/* The owner has already refunded this visit. Comping again
                      on top of a credit note is money out twice. */}
                  {f.order_credit_noted && (
                    <div className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                      This bill was already refunded by credit note.
                    </div>
                  )}

                  {f.reason_tags.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {f.reason_tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {REASON_LABELS[t] ?? t}
                        </span>
                      ))}
                    </div>
                  )}

                  {f.feedback && <p className="mt-1.5 text-sm">{f.feedback}</p>}

                  {f.recovery_state === 'recovered' && f.coupon_code && (
                    f.apology_status === 'failed' ? (
                      // The coupon exists but the message never arrived — the
                      // guest has no idea they were offered anything.
                      <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-rose-700 dark:text-rose-400">
                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                        Code <strong>{f.coupon_code}</strong> issued, but the apology
                        could not be delivered — pass it on yourself.
                      </p>
                    ) : (
                      <p className="mt-1.5 text-xs text-emerald-700 dark:text-emerald-400">
                        Comeback code <strong>{f.coupon_code}</strong>
                        {f.apology_status === 'queued'
                          ? ' issued — apology queued.'
                          : f.apology_channel === 'whatsapp_click'
                            ? ' issued — send it from your phone.'
                            : ' sent.'}
                      </p>
                    )
                  )}

                  {f.recovery_state !== 'recovered' && f.recovery_state !== 'closed' && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        disabled={!f.has_customer}
                        title={
                          f.has_customer
                            ? undefined
                            : 'Anonymous rating — there’s no guest to send a coupon to.'
                        }
                        onClick={() => setRecovering(f)}
                      >
                        <LifeBuoy className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        Apologize + send coupon
                      </Button>
                      {f.recovery_state === 'new' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setState.mutate({ id: f.id, state: 'acknowledged' })}
                        >
                          Mark acknowledged
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setState.mutate({ id: f.id, state: 'closed' })}
                      >
                        Close
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>

            {mayHaveMore && (
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPages((p) => p + 1)}
                  disabled={inboxQuery.isFetching}
                >
                  {inboxQuery.isFetching && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Load older feedback
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {recovering && (
        <RecoverGuestDialog
          row={recovering}
          onClose={() => setRecovering(null)}
          onDone={() => {
            setRecovering(null)
            invalidateAll()
          }}
        />
      )}
    </MarketingShell>
  )
}

/**
 * The confirm step. Reuses the same tier presets, override cap and reason
 * vocabulary the counter's recovery action uses (`recovery_coupon_config`), so
 * a coupon issued from here and one issued at the till are the same object
 * with the same limits.
 */
function RecoverGuestDialog({
  row,
  onClose,
  onDone,
}: {
  row: GuestFeedbackRow
  onClose: () => void
  onDone: () => void
}) {
  const { toast } = useToast()
  const [tier, setTier] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [sendMessage, setSendMessage] = useState(true)

  const configQuery = useQuery({
    queryKey: ['recovery-coupon-config'],
    queryFn: () => apiClient.getRecoveryCouponConfig(),
    staleTime: 300_000,
  })
  const cfg = configQuery.data?.data ?? null

  // The note is appended to the reason rather than dropped. The backend caps
  // the combined string at the ledger column width, so nothing 500s here.
  const ledgerReason = [reason, note.trim()].filter(Boolean).join(' — ')

  const recover = useMutation({
    mutationFn: () =>
      apiClient.recoverGuestFeedback(row.id, {
        tier,
        reason: ledgerReason || 'Low rating follow-up',
        send_message: sendMessage,
      }),
    onSuccess: (res) => {
      const channel = res.data?.message_channel
      if (channel === 'whatsapp_click' && res.data?.wa_deeplink) {
        // Nothing was sent by us — the manager taps this and sends it from
        // their own phone. This is how recovery works on a tenant with no WABA.
        window.open(res.data.wa_deeplink, '_blank', 'noopener')
      }
      toast({
        title: `Coupon ${res.data?.code} issued`,
        description:
          channel === 'none'
            ? 'No contact method on file — read the code to the guest yourself.'
            : channel === 'whatsapp_click'
              ? 'WhatsApp opened — send the message to finish.'
              : `Apology queued over ${channel === 'whatsapp_api' ? 'WhatsApp' : 'email'}.`,
      })
      onDone()
    },
    onError: (err: unknown) => {
      toast({
        title: 'Could not issue the coupon',
        description: describeApiError(err).message,
        variant: 'destructive',
      })
    },
  })

  const disabled = !cfg?.enabled || !tier || recover.isPending

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Recover {row.guest_name || 'this guest'}</DialogTitle>
        <DialogDescription>
          Issues a single-use comeback code bound to this guest — it can’t be
          shared or used by anyone else. It does not touch any current bill.
        </DialogDescription>

        {row.order_credit_noted && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 p-2.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              This visit was already refunded by credit note. A coupon on top is
              a second give — make sure that’s what you intend.
            </span>
          </div>
        )}

        {configQuery.isLoading ? (
          <div className="flex h-20 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !cfg?.enabled ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Recovery coupons are switched off. Turn them on in Settings →
            Recovery coupons to use this.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>How bad was it?</Label>
              <div className="grid grid-cols-3 gap-2">
                {cfg.tiers.map((t) => (
                  <Button
                    key={t.key}
                    type="button"
                    variant={tier === t.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTier(t.key)}
                  >
                    {t.label} · {t.percent}%
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Pick a reason" />
                </SelectTrigger>
                <SelectContent>
                  {cfg.reasons.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="rec-note">Internal note</Label>
              <Textarea
                id="rec-note"
                rows={2}
                maxLength={160}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional — appended to the reason on the audit ledger."
              />
            </div>

            <label className="flex items-start gap-2 text-sm">
              <Switch checked={sendMessage} onCheckedChange={setSendMessage} />
              <span>
                Send the apology now
                <span className="block text-xs text-muted-foreground">
                  WhatsApp if the guest opted in, otherwise email, otherwise a
                  WhatsApp link for you to send yourself. Off = issue the code
                  quietly and ring them instead.
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={recover.isPending}>
            Cancel
          </Button>
          <Button size="sm" disabled={disabled} onClick={() => recover.mutate()}>
            {recover.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Issue coupon
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
